
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface RegionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  regionToEdit?: { id: string; name: string } | null;
}

export const RegionDialog = ({ open, onOpenChange, regionToEdit }: RegionDialogProps) => {
  const [name, setName] = useState("");
  const queryClient = useQueryClient();

  useEffect(() => {
    if (regionToEdit) {
      setName(regionToEdit.name);
    } else {
      setName("");
    }
  }, [regionToEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (regionToEdit) {
        const { error } = await supabase
          .from("regions")
          .update({ name })
          .eq("id", regionToEdit.id);
        
        if (error) throw error;
        toast.success("Region updated successfully");
      } else {
        const { error } = await supabase
          .from("regions")
          .insert([{ name }]);
        
        if (error) throw error;
        toast.success("Region created successfully");
      }
      
      queryClient.invalidateQueries({ queryKey: ["regions"] });
      onOpenChange(false);
      setName("");
    } catch (error) {
      toast.error(regionToEdit ? "Failed to update region" : "Failed to create region");
      console.error("Error with region:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{regionToEdit ? "Edit Region" : "Add New Region"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Region Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter region name"
              required
            />
          </div>
          <Button type="submit">{regionToEdit ? "Update Region" : "Add Region"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
